'use client';

import Icon from '../../../../atoms/Icon';
import { cn } from '@plura/shared/utils';
import { AnimatePresence, motion } from 'framer-motion';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import "./SearchableDropdown/searchable-dropdown.css";
import { FormElementType } from '@plura/shared/types';

export interface DropdownOption {
  id: string;
  value: string;
  order: number;
}

interface SearchableDropdownProps {
  id: string;
  value?: string;
  onChange: (e: { currentTarget: { type: string; value: string } }) => void;
  options: DropdownOption[];
  placeholder?: string;
  isClearable?: boolean;
  className?: string;
  elementType: FormElementType;
  /**
   * When true (default), an Enter-key selection bubbles to the global Enter
   * handler and advances the form. Set false for dropdowns that are one field
   * among several (e.g. address country) so selecting doesn't submit the step.
   */
  advanceOnSelect?: boolean;
}

const PANEL_TRANSITION = {
  initial: { opacity: 0, scale: 0.98, y: -8 },
  animate: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1] },
  },
  exit: {
    opacity: 0,
    scale: 0.98,
    y: -8,
    transition: { duration: 0.25, ease: [0.4, 0, 1, 1] },
  },
};

const MIN_VIEWPORT_MARGIN = 40;
const SEARCH_INPUT_HEIGHT = 48;
const FOCUS_DELAY_MS = 50;
const EDGE_SCROLL_ZONE_PX = 40;
const MAX_SCROLL_SPEED_PX = 12;

function createSyntheticEvent(value: string) {
  return { currentTarget: { type: 'text', value } };
}

// --- Hooks ---

function useMenuPosition(
  isOpen: boolean,
  containerRef: React.RefObject<HTMLDivElement | null>
) {
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);

  const recalculate = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();

    const viewportHeight =
      window.visualViewport?.height ?? window.innerHeight;
    let bottomBound = viewportHeight - MIN_VIEWPORT_MARGIN;

    const mobileFooter =
      document.querySelector<HTMLElement>('[data-mobile-footer]');
    if (mobileFooter) {
      const footerRect = mobileFooter.getBoundingClientRect();
      if (footerRect.height > 0 && footerRect.top > 0) {
        bottomBound = Math.min(bottomBound, footerRect.top - 12);
      }
    }

    const available = bottomBound - rect.top - SEARCH_INPUT_HEIGHT;
    setMaxHeight(Math.max(80, available));
  }, [containerRef]);

  useLayoutEffect(() => {
    if (isOpen) recalculate();
  }, [isOpen, recalculate]);

  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener('resize', recalculate, { passive: true });
    window.addEventListener('scroll', recalculate, {
      passive: true,
      capture: true,
    });
    window.visualViewport?.addEventListener('resize', recalculate, {
      passive: true,
    });
    return () => {
      window.removeEventListener('resize', recalculate);
      window.removeEventListener('scroll', recalculate, { capture: true });
      window.visualViewport?.removeEventListener('resize', recalculate);
    };
  }, [isOpen, recalculate]);

  return { maxHeight };
}

/**
 * When the cursor hovers within EDGE_SCROLL_ZONE_PX of the top or bottom of
 * the scrollable container, smoothly auto-scrolls in that direction. Uses
 * quadratic easing so the scroll starts gently and accelerates closer to the
 * edge for a natural, Apple-like feel.
 *
 * Also tracks whether the container can scroll up/down so the component can
 * render gradient fade indicators at the overflow edges.
 */
function useEdgeAutoScroll(
  scrollContainerRef: React.RefObject<HTMLDivElement | null>,
  isActive: boolean
) {
  const rafRef = useRef(0);
  const mouseYRef = useRef<number | null>(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const updateScrollFlags = useCallback((container: HTMLElement) => {
    const threshold = 2;
    setCanScrollUp(container.scrollTop > threshold);
    setCanScrollDown(
      container.scrollTop + container.clientHeight <
        container.scrollHeight - threshold
    );
  }, []);

  useEffect(() => {
    if (!isActive) {
      mouseYRef.current = null;
      setCanScrollUp(false);
      setCanScrollDown(false);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }

    const container = scrollContainerRef.current;
    if (!container) return;

    updateScrollFlags(container);

    const onMouseMove = (e: MouseEvent) => {
      mouseYRef.current = e.clientY;
    };
    const onMouseLeave = () => {
      mouseYRef.current = null;
    };
    const onScroll = () => updateScrollFlags(container);

    container.addEventListener('mousemove', onMouseMove, { passive: true });
    container.addEventListener('mouseleave', onMouseLeave, { passive: true });
    container.addEventListener('scroll', onScroll, { passive: true });

    const tick = () => {
      const y = mouseYRef.current;
      if (y !== null && container) {
        const rect = container.getBoundingClientRect();
        const topDist = y - rect.top;
        const bottomDist = rect.bottom - y;

        if (topDist >= 0 && topDist < EDGE_SCROLL_ZONE_PX) {
          const t = 1 - topDist / EDGE_SCROLL_ZONE_PX;
          container.scrollTop -= t * t * MAX_SCROLL_SPEED_PX;
        } else if (bottomDist >= 0 && bottomDist < EDGE_SCROLL_ZONE_PX) {
          const t = 1 - bottomDist / EDGE_SCROLL_ZONE_PX;
          container.scrollTop += t * t * MAX_SCROLL_SPEED_PX;
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      container.removeEventListener('mousemove', onMouseMove);
      container.removeEventListener('mouseleave', onMouseLeave);
      container.removeEventListener('scroll', onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isActive, scrollContainerRef, updateScrollFlags]);

  return { canScrollUp, canScrollDown };
}

// --- Component ---

const SearchableDropdown = forwardRef<
  { focus: () => void } | null,
  SearchableDropdownProps
>(function SearchableDropdown(
  {
    id,
    value,
    onChange,
    options,
    placeholder = 'Type or select an option',
    isClearable = true,
    className,
    elementType,
    advanceOnSelect = true,
  },
  ref
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [isMobileView, setIsMobileView] = useState(false);

  useLayoutEffect(() => {
    const check = () => setIsMobileView(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check, { passive: true });
    return () => window.removeEventListener('resize', check);
  }, []);

  const { maxHeight: maxMenuHeight } =
    useMenuPosition(isOpen, containerRef);

  const { canScrollUp, canScrollDown } = useEdgeAutoScroll(menuRef, isOpen);

  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(
      () => searchInputRef.current?.focus(),
      FOCUS_DELAY_MS
    );
    return () => clearTimeout(timer);
  }, [isOpen]);

  // Temporarily elevate #conversation-step-wrapper above the logo/badge
  // so the fixed overlay (inside its stacking context) renders on top.
  useEffect(() => {
    if (!isMobileView || !isOpen) return;
    const wrapper = document.getElementById('conversation-step-wrapper');
    if (!wrapper) return;
    wrapper.style.zIndex = '99999';
    return () => {
      wrapper.style.zIndex = '';
    };
  }, [isOpen, isMobileView]);

  // Inside a MULTI_QUESTION_PAGE, sibling `.mqp-inline` blocks stack in DOM
  // order and would paint over the dropdown's option panel. While open,
  // bump the containing `.mqp-inline`'s z-index so its overlay wins over
  // siblings below. Cleared on close.
  useEffect(() => {
    if (!isOpen) return;
    const wrapper = containerRef.current?.closest('.mqp-inline') as HTMLElement | null;
    if (!wrapper) return;
    const prevPosition = wrapper.style.position;
    const prevZ = wrapper.style.zIndex;
    wrapper.style.position = 'relative';
    wrapper.style.zIndex = '20';
    return () => {
      wrapper.style.position = prevPosition;
      wrapper.style.zIndex = prevZ;
    };
  }, [isOpen]);

  const sortedOptions = useMemo(
    () => [...options].sort((a, b) => a.order - b.order),
    [options]
  );

  const filteredOptions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return sortedOptions;
    return sortedOptions.filter((o) => o.value.toLowerCase().includes(query));
  }, [sortedOptions, searchQuery]);

  const selectedOption = useMemo(
    () => sortedOptions.find((o) => o.id === value),
    [sortedOptions, value]
  );

  const open = useCallback(() => {
    setIsOpen(true);
    setTimeout(() => searchInputRef.current?.focus(), FOCUS_DELAY_MS);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
    setFocusedIndex(-1);
  }, []);

  useImperativeHandle(ref, () => ({ focus: open }), [open]);

  const handleSelect = useCallback(
    (option: DropdownOption) => {
      onChange(createSyntheticEvent(option.id));
      close();
      // When selection doesn't advance the form, hand focus back to the
      // trigger so Tab/Enter keep flowing through the remaining fields.
      if (!advanceOnSelect) {
        requestAnimationFrame(() => triggerRef.current?.focus());
      }
    },
    [onChange, close, advanceOnSelect]
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange(createSyntheticEvent(''));
      close();
    },
    [onChange, close]
  );

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        !containerRef.current?.contains(target) &&
        !target.closest('[data-dropdown-panel]')
      ) {
        close();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, close]);

  const handlePanelKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          close();
          break;
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex((i) =>
            i < filteredOptions.length - 1 ? i + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex((i) =>
            i > 0 ? i - 1 : filteredOptions.length - 1
          );
          break;
        case 'Home':
          e.preventDefault();
          setFocusedIndex(0);
          break;
        case 'End':
          e.preventDefault();
          setFocusedIndex(filteredOptions.length - 1);
          break;
        case 'Enter':
          e.preventDefault();
          if (!advanceOnSelect) e.stopPropagation();
          if (focusedIndex >= 0 && filteredOptions[focusedIndex]) {
            handleSelect(filteredOptions[focusedIndex]);
          }
          break;
        case 'Tab':
          close();
          break;
      }
    },
    [filteredOptions, focusedIndex, handleSelect, close]
  );

  const handleTriggerKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        // Opening the picker should never double as a form submit.
        e.stopPropagation();
        open();
      }
    },
    [open]
  );

  useEffect(() => {
    if (focusedIndex < 0 || !listRef.current) return;
    const el = listRef.current.children[focusedIndex] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [focusedIndex]);

  const triggerControl = (
    <div
      ref={triggerRef}
      className={'control'}
      onClick={open}
      onKeyDown={handleTriggerKeyDown}
      tabIndex={0}
      role="combobox"
      aria-expanded={isOpen}
      aria-haspopup="listbox"
      aria-controls={`${id}-listbox`}
      aria-label={placeholder}
    >
      <div className={'valueContainer'}>
        <div className={'singleValue'}>
          {selectedOption ? selectedOption.value : placeholder}
        </div>
      </div>
      <div className="indicators">
        {isClearable && value ? (
          <div
            className={'indicator'}
            onClick={handleClear}
            onMouseDown={(e) => e.preventDefault()}
            role="button"
            tabIndex={-1}
            aria-label="Clear selection"
          >
            <Icon name="close" className="!size-[1em] shrink-0 fill-current" />
          </div>
        ) : (
          <div className={'indicatorIconWrapper'}>
            <Icon
              name="chevron"
              className={cn(
                'indicatorIcon !size-full max-h-full max-w-full shrink-0 fill-current',
                !isOpen && 'indicatorIconVisible',
              )}
            />
          </div>
        )}
      </div>
    </div>
  );

  const panelInner = (
    <>
      <div className={'panelSearch'}>
        <input
          ref={searchInputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setFocusedIndex(0);
          }}
          placeholder="Search options..."
          autoComplete="off"
          aria-label="Search options"
          aria-autocomplete="list"
          aria-controls={`${id}-listbox`}
          aria-activedescendant={
            focusedIndex >= 0 && filteredOptions[focusedIndex]
              ? `${id}-option-${filteredOptions[focusedIndex].id}`
              : undefined
          }
        />
        <Icon name="search" className="panelSearchIcon shrink-0 fill-current" />
      </div>
      <div className={'panelMenuWrapper'}>
        <div
          className={cn(
            'scrollIndicator',
            'scrollIndicatorTop',
            canScrollUp && 'scrollIndicatorVisible'
          )}
          aria-hidden="true"
        />
        <div
          className={'panelMenu'}
          ref={menuRef}
          role="listbox"
          id={`${id}-listbox`}
        >
          <ul
            className={'menuList'}
            ref={listRef}
            onMouseLeave={() => setFocusedIndex(-1)}
          >
            {filteredOptions.length === 0 ? (
              <li className={'menuNotice'} role="status">
                No options found
              </li>
            ) : (
              filteredOptions.map((option, index) => {
                const isSelected = option.id === value;
                const isFocused = index === focusedIndex;
                return (
                  <li
                    key={option.id}
                    id={`${id}-option-${option.id}`}
                    className={cn(
                      'option',
                      isFocused && 'optionFocused',
                      isSelected && 'optionSelected'
                    )}
                    role="option"
                    aria-selected={isSelected}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelect(option);
                    }}
                    onMouseEnter={() => setFocusedIndex(index)}
                  >
                    <span>{option.value}</span>
                    {isSelected ? (
                      <span className="optionCheck" aria-hidden>
                        <Icon name="check" className="optionCheckIcon shrink-0 fill-current" />
                      </span>
                    ) : null}
                  </li>
                );
              })
            )}
          </ul>
        </div>
        <div
          className={cn(
            'scrollIndicator',
            'scrollIndicatorBottom',
            canScrollDown && 'scrollIndicatorVisible'
          )}
          aria-hidden="true"
        />
      </div>
    </>
  );

  const overlayPanel = (
    <motion.div
      data-dropdown-panel
      className={cn('overlayPanel', elementType)}
      {...PANEL_TRANSITION}
      onKeyDown={handlePanelKeyDown}
      style={
        maxMenuHeight !== undefined
          ? { ['--max-menu-height' as string]: `${maxMenuHeight}px` }
          : {}
      }
    >
      {panelInner}
    </motion.div>
  );

  const mobileOverlay = (
    <motion.div
      className="mobileDropdownOverlay"
      data-dropdown-panel
      initial={{ opacity: 0 }}
      animate={{
        opacity: 1,
        transition: { duration: 0.25 },
      }}
      exit={{
        opacity: 0,
        transition: { duration: 0.2 },
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
      onKeyDown={handlePanelKeyDown}
    >
      <div
        className="mobileDropdownClose"
        onClick={close}
        onMouseDown={(e) => e.preventDefault()}
        role="button"
        tabIndex={-1}
        aria-label="Close"
      >
        <Icon name="close" className="!size-5 shrink-0 fill-current" />
      </div>
      <motion.div
        className={cn('overlayPanel', elementType)}
        initial={{ opacity: 0, y: 30 }}
        animate={{
          opacity: 1,
          y: 0,
          transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] },
        }}
        exit={{
          opacity: 0,
          y: 30,
          transition: { duration: 0.2 },
        }}
      >
        {panelInner}
      </motion.div>
    </motion.div>
  );

  return (
    <div ref={containerRef} id={id} className={cn('searchableDropdownContainer', className)}>
      {triggerControl}
      <AnimatePresence>
        {isOpen &&
          (isMobileView ? (
            mobileOverlay
          ) : (
            <div className={'overlayPanelWrapper'}>{overlayPanel}</div>
          ))}
      </AnimatePresence>
    </div>
  );
});

export default SearchableDropdown;
